import React, { useEffect, useMemo, useRef, useState } from "react";

const apiBase = () =>
  window.TOPIC_BOT_API ||
  process.env.REACT_APP_TOPIC_BOT_API ||
  "";

const gateKey = () =>
  (new URLSearchParams(window.location.search).get("tb")) ||
  process.env.REACT_APP_TOPIC_BOT_KEY ||
  "";

async function postJSON(path, payload) {
  const r = await fetch(`${apiBase()}${path}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-topic-bot-key": gateKey()
    },
    body: JSON.stringify(payload || {})
  });
  const body = await r.json().catch(() => ({}));
  return { status: r.status, body };
}

async function getJSON(path) {
  const r = await fetch(`${apiBase()}${path}`, {
    headers: { "x-topic-bot-key": gateKey() }
  });
  const body = await r.json().catch(() => ({}));
  return { status: r.status, body };
}

export default function TopicBotWidget() {
  const [unlocked, setUnlocked] = useState(false);
  const [pass, setPass] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const requiredKey = useMemo(() => gateKey(), []);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const [booting, setBooting] = useState(false);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, metrics]);

  useEffect(() => {
    if (unlocked) inputRef.current?.focus();
  }, [unlocked]);

  // focus after assistant message (or when no longer busy)
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role === "assistant" && !busy) inputRef.current?.focus();
  }, [messages, busy]);

  // Gate: allow query param ?tb=<key> or stored unlock
  useEffect(() => {
    if (!requiredKey) { setUnlocked(true); return; }
    const stored = sessionStorage.getItem("topicBotUnlocked") === "1";
    const qp = new URLSearchParams(window.location.search).get("tb");
    if (qp && qp === requiredKey) {
      sessionStorage.setItem("topicBotUnlocked", "1");
      setUnlocked(true);
    } else if (stored) {
      setUnlocked(true);
    }
  }, [requiredKey]);

  useEffect(() => {
    if (unlocked && !sessionId) {
      (async () => {
        try { await ensureSession(); } catch (_) {}
      })();
    }
  }, [unlocked, sessionId]);

  function Spinner({label="Loading…"}) {
    return (
      <div style={{display:"flex",alignItems:"center",gap:8,opacity:0.8}}>
        <div style={{
          width:14,height:14,border:"2px solid #666",borderTopColor:"#fff",
          borderRadius:"50%", animation:"spin 0.8s linear infinite"
        }} />
        <span style={{fontSize:12}}>{label}</span>
        <style>{`@keyframes spin {to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  async function ensureSession() {
    if (sessionId) return sessionId;
    setBooting(true);
    try {
      const { status, body } = await postJSON("/chat/send", {});
      if (status === 201 && body.session_id) {
        setSessionId(body.session_id);
        if (body.assistant_message) {
          setMessages(m => [...m, { role: "assistant", text: body.assistant_message }]);
        }
        return body.session_id;
      }
      throw new Error("Failed to create session");
    } finally {
      setBooting(false);
    }
  }

  async function pollMetrics(sid, tries = 10) {
    for (let i = 0; i < tries; i++) {
      const { status, body } = await getJSON(`/session/${sid}/metrics`);
      if (status === 200 && body.status === "ready") {
        setMetrics(body.metrics);
        return;
      }
      if (status !== 202) break;
      await new Promise(r => setTimeout(r, 1000));
    }
  }

  async function send() {
    if (!input.trim() || busy) return;
    setBusy(true);
    try {
      const sid = await ensureSession();
      // optimistic user echo
      setMessages(m => [...m, { role: "user", text: input }]);
      const payload = { session_id: sid, message: input };
      setInput("");
      const { status, body } = await postJSON("/chat/send", payload);
      const assistant = body?.assistant_message || (status === 200 ? "(ok)" : "(error)");
      setMessages(m => [...m, { role: "assistant", text: assistant, policy: body?.policy }]);

      // If policy indicates completion, fetch metrics
      const action = body?.policy?.action || body?.topic_status;
      if (action === "complete" || action === "completed") {
        await pollMetrics(sid);
      }
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", text: `Error: ${e.message}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function newSession() {
    setSessionId(null);
    setMessages([]);
    setMetrics(null);
    const { status, body } = await postJSON("/chat/send", {});
    if (status === 201 && body.session_id) {
      setSessionId(body.session_id);
      if (body.assistant_message) {
        setMessages(m => [...m, { role:"assistant", text: body.assistant_message }]);
      }
    } else {
      throw new Error("Failed to create session");
    }
  }


  function unlock() {
    if (pass && pass === requiredKey) {
      sessionStorage.setItem("topicBotUnlocked", "1");
      setUnlocked(true);
    }
  }

  // ——— UI ———
  if (!apiBase()) {
    return <div style={box}><h3>TopicBot (dev)</h3><p>Missing API base. Set <code>REACT_APP_TOPIC_BOT_API</code>.</p></div>;
  }

  if (!unlocked) {
    return (
      <div style={box}>
        <h3>TopicBot (gated)</h3>
        <p>Enter passphrase to unlock.</p>
        <input
          ref={inputRef}
          style={inputStyle}
          type="password"
          value={pass}
          onChange={e => setPass(e.target.value)}
          placeholder="passphrase"
        />
        <button style={btn} onClick={unlock}>Unlock</button>
        <p style={{fontSize:12,opacity:0.7,marginTop:8}}>
          Tip: you can also append <code>?tb=…</code> to the URL.
        </p>
      </div>
    );
  }

  return (
    <div style={box}>
      <h3>TopicBot (Sustainable Business MVP)</h3>

      {booting && messages.length === 0 && (
        <div style={{marginBottom:8}}><Spinner label="Starting conversation…" /></div>
      )}

      <div style={transcript}>
        {messages.map((m, i) => (
          <div key={i} style={{...bubble, ...(m.role === "user" ? userBubble : assistantBubble)}}>
            <div style={{fontSize:12, opacity:0.7}}>{m.role}</div>
            <div>{m.text}</div>
            {m.policy && <pre style={policy}>{JSON.stringify(m.policy, null, 0)}</pre>}
          </div>
        ))}
        {metrics && (
          <div style={{...bubble, ...assistantBubble}}>
            <div style={{fontWeight:600}}>Metrics</div>
            <pre style={policy}>{JSON.stringify(metrics, null, 2)}</pre>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div>
        <textarea
          ref={inputRef}
          style={{
            ...inputStyle,
            width: "100%",            // full width
            minHeight: 96,            // a bit taller
            lineHeight: "1.4",
            resize: "vertical",
            boxSizing: "border-box",
            display: "block"
          }}
          value={input}
          disabled={busy}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();     // submit on Enter
              send();
            }
            // Shift+Enter inserts newline by default
          }}
          placeholder="Type your message… (Shift+Enter for newline)"
        />
        <div style={{ ...row, marginTop: 8 }}>
          <button style={btn} disabled={busy} onClick={send}>{busy ? "…" : "Send"}</button>
          <button style={btn} onClick={newSession}>New</button>
        </div>
      </div>

      {/* <div style={{fontSize:12, opacity:0.7, marginTop:4}}>
        Tip: Press <b>Shift+Enter</b> for a new line.
      </div> */}

      <div style={{fontSize:12,opacity:0.7,marginTop:6}}>
        API: <code>{apiBase()}</code> {sessionId ? <>· session <code>{sessionId.slice(0,8)}…</code></> : null}
      </div>
      
    </div>
  );
}

// inline styles (keeps this self-contained)
const box = { maxWidth: 800, margin: "16px auto", padding: 12, border: "1px solid #333", borderRadius: 8, background:"#111", color:"#eee" };
const transcript = { maxHeight: 360, overflowY: "auto", padding: 8, background:"#0b0b0b", borderRadius: 6, marginBottom: 8, border:"1px solid #222" };
const bubble = { marginBottom: 8, padding: 8, borderRadius: 6 };
const userBubble = { background:"#1a2a1a", border:"1px solid #2c3" };
const assistantBubble = { background:"#1a1a2a", border:"1px solid #46f" };
const policy = { marginTop: 6, background:"#121212", border:"1px dashed #444", padding:6, borderRadius:4, whiteSpace:"pre-wrap" };
const row = { display:"flex", gap:8, alignItems:"center" };
const inputStyle = { flex:1, padding:"8px 10px", borderRadius:6, border:"1px solid #333", background:"#0e0e0e", color:"#eee" };
const btn = { padding:"8px 12px", borderRadius:6, border:"1px solid #333", background:"#222", color:"#fff", cursor:"pointer" };
